import { NgForOf, NgIf } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectChange, MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsAbstractControlComponent } from '@abstract/components/ds-abstract-control.component';
import {
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  OnInit,
  Input,
  inject,
  Optional,
  Output,
  QueryList,
  Self,
  ViewChild,
} from '@angular/core';

/**
 * Reusable Angular Material select wrapper that integrates seamlessly
 * with reactive forms (`FormControl` / `NgControl`) and supports:
 * - Content projection of `MatOption` and `MatSelectTrigger`
 * - An `options` input — projected `mat-option` does not sync reliably with async lists; use `[options]` instead
 * - Custom `compareWith` function for object comparison
 * - ARIA attributes for accessibility
 * - Dynamic panel class styling
 * - Full ControlValueAccessor support for `formControl` and `formControlName`
 */
@Component({
  selector: 'ds-select',
  templateUrl: './ds-select.component.html',
  styleUrls: ['./ds-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [NgIf, NgForOf, MatIconModule, MatSelectModule, ReactiveFormsModule, DsInputComponent],
})
export class DsSelectComponent extends DsAbstractControlComponent implements AfterViewInit, OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(MatSelect) matSelect!: MatSelect;

  @ViewChild('searchInput') private readonly searchInput?: DsInputComponent;

  @ContentChildren(MatOption, { descendants: true }) contentOptions!: QueryList<MatOption>;

  @ContentChildren(MatSelectTrigger) contentTrigger!: QueryList<MatSelectTrigger>;

  /** Custom panel class for the dropdown */
  @Input() panelClass?: string | string[];

  /**
   * Whether the dropdown is searchable. When left unset, the search field is shown
   * automatically once the number of options exceeds `searchOptionThreshold`.
   * An explicit value always overrides the automatic behavior.
   */
  @Input() isSearchable?: boolean;

  /** Number of options above which the search field auto-enables */
  private readonly searchOptionThreshold = 5;

  /** Whether the search field is currently rendered */
  showSearch = false;

  /** Async option lists — avoids a sync issue with content-projected `mat-option`. Ignores projected content when set. */
  @Input() set options(options: BaseDropdownInterface[] | undefined) {
    if (options === undefined) {
      return;
    }

    this.hasOptionsInput = true;
    this.allOptions = options;
    this.resetSearchFilter();
    this.filterInputOptions('');
    this.changeDetectorRef.markForCheck();

    if (this.matSelect) {
      this.refreshSelectionDisplay();
    }
  }

  /** Function to compare selected value with option value */
  @Input() compareWith?: (o1: unknown, o2: unknown) => boolean;

  /** Emits when the selection changes (mirrors MatSelect's selectionChange) */
  @Output() selectionChange = new EventEmitter<MatSelectChange>();

  /** Default comparison function if `compareWith` is not provided */
  readonly defaultCompareWith = (o1: unknown, o2: unknown): boolean => o1 === o2;

  /** Form control for search input */
  readonly autocompleteControl = new FormControl<string>('', { nonNullable: true });

  /** Indicates there are no options matching the current filter */
  noOptionsMatchingFilter = false;

  /** Indicates the option list is empty */
  noOptionsAvailable = false;

  /** Whether the select is disabled */
  disabled = false;

  /** Whether options are supplied through the `options` input */
  hasOptionsInput = false;

  /** Full list of options supplied through the `options` input */
  allOptions: BaseDropdownInterface[] = [];

  /** Options shown after applying the search filter */
  visibleOptions: BaseDropdownInterface[] = [];

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    if (!this.hasOptionsInput) {
      this.subscribeOnMatSelectOptionsChange();
      this.initProjectedOptions();
      this.subscribeOnContentOptionsChange();
    } else {
      this.refreshSelectionDisplay();
    }

    this.initTrigger();
    this.subscribeOnContentTriggerChange();
    this.updateSearchVisibility();

    if (this.showSearch) {
      this.subscribeOnPanelOpenChange();
    }
  }

  ngOnInit(): void {
    const autocompleteSubscription = this.autocompleteControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((value: string): void => {
        if (this.hasOptionsInput) {
          this.filterInputOptions(value);
          return;
        }

        this.applyProjectedOptionsFilter(value);
      });

    this.subscriptions.add(autocompleteSubscription);
  }

  /**
   * TrackBy function for options supplied through the `options` input.
   *
   * @param _index - option index
   * @param option - dropdown option
   */
  trackByOptionId(_index: number, option: BaseDropdownInterface): string {
    return option.id;
  }

  writeValue(_obj: unknown): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Subscribes to panel open/close and manages search input focus and filter reset.
   */
  private subscribeOnPanelOpenChange(): void {
    const subscription = this.matSelect.openedChange.subscribe((isOpen: boolean): void => {
      if (isOpen) {
        this.focusSearchInput();
        return;
      }

      this.resetSearchFilter();

      if (this.hasOptionsInput) {
        this.filterInputOptions('');
        return;
      }

      this.applyProjectedOptionsFilter('');
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Clears the search input without emitting value changes.
   */
  private resetSearchFilter(): void {
    this.autocompleteControl.setValue('', { emitEvent: false });
  }

  /**
   * Defers focus until the overlay panel and nested search input are rendered.
   */
  private focusSearchInput(): void {
    queueMicrotask((): void => {
      this.searchInput?.focus();
    });
  }

  /**
   * Subscribes to changes in the projected MatSelectTrigger elements
   * and updates the internal MatSelect trigger
   */
  private subscribeOnContentTriggerChange(): void {
    const subscription = this.contentTrigger.changes.subscribe((): void => {
      this.initTrigger();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Subscribes to changes in the projected MatOption elements
   * and re-initializes the internal MatSelect options
   */
  private subscribeOnContentOptionsChange(): void {
    const subscription = this.contentOptions.changes.subscribe(() => {
      this.initProjectedOptions();
      this.updateSearchVisibility();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Keeps projected options registered with MatSelect when the searchable variant is used.
   *
   * The sticky search field is a `mat-option` living in this component's own view, so it is a
   * native content child of the internal `MatSelect`. When it renders, Angular's own option query
   * overwrites the projected options that `initProjectedOptions` injected — leaving MatSelect with
   * only the search option, which drops any preselected value and shows an empty placeholder.
   * Re-inject the projected options whenever that native query changes.
   */
  private subscribeOnMatSelectOptionsChange(): void {
    const nativeOptions: QueryList<MatOption> = this.matSelect.options;

    const subscription = nativeOptions.changes.subscribe((): void => {
      // The search mat-option is a native child of MatSelect, so this fires when it (dis)appears.
      // Angular reassigns MatSelect.options to its native query result immediately AFTER this event,
      // clobbering the projected options. Defer the re-injection to a microtask so it runs after
      // that reassignment.
      queueMicrotask((): void => {
        if (this.hasOptionsInput || this.contentOptions.length === 0) {
          return;
        }

        if (this.matSelect.options !== this.contentOptions) {
          this.initProjectedOptions();
        }
      });
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Determines search-field visibility: an explicit `isSearchable` input wins;
   * otherwise the field auto-enables when option count exceeds the threshold.
   */
  private updateSearchVisibility(): void {
    const optionsCount = this.hasOptionsInput ? this.allOptions.length : (this.contentOptions?.length ?? 0);
    this.showSearch = this.isSearchable ?? optionsCount > this.searchOptionThreshold;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Initializes internal MatSelect options from projected content.
   */
  private initProjectedOptions(): void {
    if (this.contentOptions.length === 0) {
      this.applyProjectedOptionsFilter(this.autocompleteControl.value);
      return;
    }

    this.matSelect.options = this.contentOptions;

    this.matSelect.ngAfterContentInit();
    this.applyProjectedOptionsFilter(this.autocompleteControl.value);
    this.refreshSelectionDisplay();
  }

  /**
   * Re-applies the control value after mat-options are available so MatSelect shows the label on load.
   */
  private refreshSelectionDisplay(): void {
    const control: FormControl | undefined = this.control;

    if (!control || control.value === null || control.value === undefined || control.value === '') {
      return;
    }

    const currentValue: unknown = control.value;

    queueMicrotask((): void => {
      this.matSelect?.writeValue(currentValue);
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * Filters options supplied through the `options` input.
   *
   * @param value - search text
   */
  private filterInputOptions(value: string): void {
    const filterValue: string = value.toLowerCase().trim();

    this.noOptionsAvailable = this.allOptions.length === 0;
    this.visibleOptions = filterValue
      ? this.allOptions.filter((option: BaseDropdownInterface): boolean => option.name.toLowerCase().includes(filterValue))
      : [...this.allOptions];
    this.noOptionsMatchingFilter = filterValue.length > 0 && this.allOptions.length > 0 && this.visibleOptions.length === 0;
    this.changeDetectorRef.markForCheck();
    this.refreshSelectionDisplay();
  }

  /**
   * Filters projected mat-options for searchable selects.
   *
   * @param value - search text
   */
  private applyProjectedOptionsFilter(value: string): void {
    if (!this.showSearch || !this.contentOptions) {
      this.noOptionsMatchingFilter = false;
      this.noOptionsAvailable = false;
      return;
    }

    const filterValue: string = value.toLowerCase().trim();

    if (this.contentOptions.length === 0) {
      this.noOptionsAvailable = true;
      this.noOptionsMatchingFilter = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.noOptionsAvailable = false;
    let matchingOptionsCount = 0;
    const optgroupVisibility = new Map<HTMLElement, boolean>();

    this.contentOptions.forEach((option: MatOption): void => {
      const shouldShowOption: boolean = option.viewValue.toLowerCase().includes(filterValue);
      const optionElement = (option as MatOption & { _getHostElement?: () => HTMLElement })._getHostElement?.();

      if (optionElement) {
        optionElement.style.display = shouldShowOption ? '' : 'none';
        const optgroupElement = optionElement.closest('mat-optgroup') as HTMLElement | null;

        if (optgroupElement) {
          optgroupVisibility.set(optgroupElement, (optgroupVisibility.get(optgroupElement) ?? false) || shouldShowOption);
        }
      }

      if (shouldShowOption) {
        matchingOptionsCount += 1;
      }
    });

    // Hide a section header when all of its options are filtered out
    optgroupVisibility.forEach((hasVisibleOption: boolean, optgroupElement: HTMLElement): void => {
      optgroupElement.style.display = hasVisibleOption ? '' : 'none';
    });

    this.noOptionsMatchingFilter = filterValue.length > 0 && matchingOptionsCount === 0;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Initializes the custom trigger for MatSelect if provided
   */
  private initTrigger(): void {
    if (this.contentTrigger.length) {
      this.matSelect.customTrigger = this.contentTrigger.first;
    }
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }
}
